import { useEffect, useMemo, useState } from "react";
import { Search, Trash2, Save, Send, Star, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, pln, API } from "@/lib/api";
import { commissionPerPiece, haftAddForItem } from "@/lib/commission";
import { ItemRow } from "./ItemRow";
import { FileUploader } from "./FileUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const EMPTY_CUST = { name: "", short_name: "", nip: "", contact: "", email: "", phone: "", address: "" };
const CUST_FIELDS = [["name", "Nazwa firmy *"], ["short_name", "Nazwa skrócona *"], ["contact", "Osoba kontaktowa"], ["phone", "Telefon"], ["email", "E-mail"], ["address", "Adres"]];

const Section = ({ n, title, children }) => (
  <section className="rounded-xl border border-border bg-card p-5 space-y-4">
    <h3 className="font-display text-sm font-bold uppercase tracking-wider flex items-center gap-2"><span className="font-mono text-amber-400">{n}</span>{title}</h3>
    {children}
  </section>
);

const ProductPicker = ({ products, premiumSkus, onPick, testid, label, highlight = false }) => {
  const [open, setOpen] = useState(false);
  const sorted = [...products].sort((a, b) => (premiumSkus?.has(b.sku) ? 1 : 0) - (premiumSkus?.has(a.sku) ? 1 : 0));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testid}
          className={`gap-2 w-full sm:w-auto ${highlight ? "bg-amber-400 text-[#0D1117] border-amber-400 hover:bg-amber-500 font-bold uppercase tracking-wider" : "border-dashed"}`}><Search className="h-4 w-4" />{label}</Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]">
        <Command filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
          <CommandInput data-testid={`${testid}-search`} placeholder="Szukaj: nazwa, SKU, typ, marka…" />
          <CommandList>
            <CommandEmpty>Brak wyników.</CommandEmpty>
            <CommandGroup heading="★ Z progami prowizji na górze">
              {sorted.map((p) => {
                const premium = premiumSkus?.has(p.sku);
                return (
                <CommandItem key={p.sku} value={`${p.sku} ${p.name} ${p.type} ${p.brand || ""}`} data-testid={`picker-option-${p.sku}`}
                  onSelect={() => { onPick(p.sku); setOpen(false); }}>
                  {premium
                    ? <Star className="mr-1.5 h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
                    : <span className="mr-1.5 w-3.5 shrink-0" />}
                  <span className="font-mono text-xs font-bold text-amber-500 mr-2 w-20 shrink-0 truncate">{p.type}</span>
                  <span className={`font-semibold ${premium ? "text-amber-200" : ""}`}>{p.sku}</span>
                  <span className="ml-2 truncate text-xs text-muted-foreground">{p.name}</span>
                </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const CustomerPicker = ({ customers, value, onPick }) => {
  const [open, setOpen] = useState(false);
  const sel = customers.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" data-testid="customer-select" className="w-full justify-between font-normal">
          <span className={`truncate ${sel ? "" : "text-muted-foreground"}`}>{sel ? (sel.short_name || sel.name) : "Wybierz klienta z listy"}</span>
          <Search className="h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]">
        <Command filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
          <CommandInput data-testid="customer-select-search" placeholder="Szukaj: nazwa, NIP, adres…" />
          <CommandList>
            <CommandEmpty>Brak klientów.</CommandEmpty>
            <CommandGroup>
              {customers.map((c) => (
                <CommandItem key={c.id} value={`${c.short_name || ""} ${c.name || ""} ${c.nip || ""} ${c.address || ""}`} data-testid={`customer-option-${c.id}`}
                  onSelect={() => { onPick(c.id); setOpen(false); }}>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{c.short_name || c.name}</div>
                    {(c.address || c.nip) && <div className="truncate text-xs text-muted-foreground">{[c.nip, c.address].filter(Boolean).join(" · ")}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const NewOrderForm = ({ catalog, customers, onCreated, editOrder, onCancelEdit, isAdmin = false, users = [], presetCustomerId, presetOwnerId, presetProductSku, onCustomersChanged }) => {
  const products = catalog.products;
  const rules = catalog.commission_rules || {};
  const premiumSkus = useMemo(() => new Set(Object.keys(rules).filter((k) => (rules[k]?.tiers || []).length > 0)), [rules]);
  const isEdit = !!editOrder;
  const { user } = useAuth();
  const DRAFT_KEY = `empro_draft_${user?.id || "anon"}`;
  const draft = (() => { if (isEdit) return null; try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; } })();
  const initItems = () => isEdit
    ? editOrder.items.map((it) => ({ sku: it.sku, color: it.color, sizes: { ...it.sizes }, unit_price: it.unit_price, notes: it.notes || "", viz_id: it.viz_id || null,
        custom_commission: !!it.custom_commission, custom_commission_per_piece: it.custom_commission_per_piece ?? it.commission_per_piece ?? 0,
        branding: it.branding ? { type: it.branding.type, placements: [...(it.branding.placements || [])] } : { type: editOrder.branding?.type || "DTF", placements: [...(editOrder.branding?.placements || [])] } }))
    : (draft?.items || []);
  const [ownerId, setOwnerId] = useState(isAdmin ? (isEdit ? (editOrder.employee_id || "") : (presetOwnerId || draft?.ownerId || users[0]?.id || "")) : "");
  const [adminCustomers, setAdminCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(isEdit ? (editOrder.customer_id || "") : (presetCustomerId || draft?.customerId || ""));
  const [items, setItems] = useState(initItems);
  const [notes, setNotes] = useState(isEdit ? editOrder.notes : (draft?.notes || ""));
  const [createdDate, setCreatedDate] = useState(isEdit ? (editOrder.created_at || "").slice(0, 10) : "");
  const [files, setFiles] = useState(isEdit ? (editOrder.files || []) : (draft?.files || []));
  const [busy, setBusy] = useState(false);
  const [extraCustomers, setExtraCustomers] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [newCust, setNewCust] = useState(EMPTY_CUST);
  const [custBusy, setCustBusy] = useState(false);
  const [allVizs, setAllVizs] = useState([]);
  const [changeOwnerId, setChangeOwnerId] = useState("");

  useEffect(() => {
    if (!isAdmin || !ownerId) return;
    api.get("/customers", { params: { employee_id: ownerId } }).then((r) => {
      setAdminCustomers(r.data);
      setCustomerId((cur) => (r.data.some((c) => c.id === cur) ? cur : ""));
    }).catch(() => {});
  }, [isAdmin, ownerId]);

  useEffect(() => {
    if (isEdit) return;
    const has = items.length || notes || customerId || files.length;
    if (has) localStorage.setItem(DRAFT_KEY, JSON.stringify({ items, notes, customerId, ownerId, files }));
    else localStorage.removeItem(DRAFT_KEY);
  }, [isEdit, items, notes, customerId, ownerId, files, DRAFT_KEY]);

  useEffect(() => {
    api.get("/visualizations").then((r) => setAllVizs(r.data || [])).catch(() => setAllVizs([]));
  }, []);

  // ——— Wizualizacje klienta z kartoteki (do dołączenia do zamówienia) ———
  const clientVizList = useMemo(() => allVizs.filter((v) => v.client_id === customerId), [allVizs, customerId]);
  const vizTitle = (v) => [v.title_text, v.subtitle_text].filter(Boolean).join(" — ") || v.title || v.product_name || "Wizualizacja";
  const vizAttachFileId = (v) => v.sheet_pdf_id || v.pdf_file_id || null;
  const vizThumbId = (v) => v.thumb_image_id || v.front_image_id || v.base_image_id || null;
  const isClientVizAttached = (v) => { const fid = vizAttachFileId(v); return !!fid && files.some((f) => f.id === fid); };
  const toggleClientViz = (v) => {
    const fid = vizAttachFileId(v);
    if (!fid) { toast.error("Ta wizualizacja nie ma zapisanego arkusza PDF — otwórz ją w edytorze i zapisz."); return; }
    setFiles((prev) => prev.some((f) => f.id === fid)
      ? prev.filter((f) => f.id !== fid)
      : [...prev, { id: fid, filename: `${vizTitle(v)}.pdf`, content_type: "application/pdf", viz: true }]);
  };

  const baseCustomerList = isAdmin ? adminCustomers : customers;
  const customerList = useMemo(() => {
    const seen = new Set(baseCustomerList.map((c) => c.id));
    return [...baseCustomerList, ...extraCustomers.filter((c) => !seen.has(c.id))];
  }, [baseCustomerList, extraCustomers]);

  const fetchGusNew = async () => {
    const nip = (newCust.nip || "").trim();
    if (!nip) return toast.error("Wpisz NIP");
    setCustBusy(true);
    try {
      const { data } = await api.get(`/gus/${nip}`);
      setNewCust((c) => ({ ...c, name: data.name || c.name, short_name: c.short_name || (data.name || "").slice(0, 40), address: data.address || c.address, contact: data.contact || c.contact, phone: data.phone || c.phone, email: data.email || c.email }));
      toast.success("Pobrano dane z rejestru (Biała Lista MF)");
    } catch (e) { toast.error(errMsg(e)); } finally { setCustBusy(false); }
  };

  const openCustDialog = () => {
    if (isAdmin && !ownerId) return toast.error("Najpierw wybierz handlowca");
    setNewCust(EMPTY_CUST); setCustOpen(true);
  };

  const saveNewCustomer = async () => {
    if (!newCust.name.trim()) return toast.error("Podaj nazwę firmy");
    if (!newCust.short_name.trim()) return toast.error("Podaj nazwę skróconą");
    setCustBusy(true);
    try {
      const payload = { ...newCust };
      if (isAdmin) payload.employee_id = ownerId;
      const { data } = await api.post("/customers", payload);
      setExtraCustomers((prev) => (prev.some((c) => c.id === data.id) ? prev : [...prev, data]));
      setCustomerId(data.id);
      setCustOpen(false);
      toast.success(`Klient „${data.short_name || data.name}" dodany`);
      onCustomersChanged && onCustomersChanged();
    } catch (e) {
      const msg = errMsg(e);
      if (e?.response?.status === 409 && msg.toUpperCase().startsWith("KLIENT:")) {
        toast.error(`Klient już przypisany do handlowca: ${msg.replace(/^KLIENT:\s*/i, "").trim()}`);
      } else { toast.error(msg); }
    } finally { setCustBusy(false); }
  };

  const totals = useMemo(() => items.reduce((t, it) => {
    const qty = Object.values(it.sizes).reduce((a, b) => a + (Number(b) || 0), 0);
    const c = commissionPerPiece(it.sku, it.unit_price, it.branding?.type, it.branding?.placements || [], rules);
    const effComm = it.custom_commission ? (Number(it.custom_commission_per_piece) || 0) : c.commission;
    const hasNote = it.custom_commission ? false : !!c.note;
    const haftAdd = haftAddForItem(it.sku, it.branding, rules);
    return { qty: t.qty + qty, net: t.net + qty * ((Number(it.unit_price) || 0) + haftAdd), comm: t.comm + qty * effComm, indiv: t.indiv || (qty > 0 && hasNote) };
  }, { qty: 0, net: 0, comm: 0, indiv: false }), [items, rules]);

  const addItem = (sku) => {
    const p = products.find((x) => x.sku === sku);
    if (!p) return;
    const bt = (p.branding_types?.length ? p.branding_types[0] : "DTF");
    setItems((prev) => {
      const next = [...prev, { sku, color: "", sizes: {}, unit_price: "", notes: "", viz_id: null, branding: { type: bt, placements: [] } }];
      const newIdx = next.length - 1;
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="item-row-${newIdx}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
      return next;
    });
  };

  useEffect(() => {
    if (isEdit || !presetProductSku) return;
    setItems((prev) => (prev.some((x) => x.sku === presetProductSku) ? prev : [...prev, {
      sku: presetProductSku, color: "", sizes: {}, unit_price: "", notes: "", viz_id: null,
      branding: { type: (products.find((x) => x.sku === presetProductSku)?.branding_types?.[0]) || "DTF", placements: [] } }]));
  }, []); // eslint-disable-line

  const clearCart = async () => {
    if (!(await confirm({ title: "Wyczyścić koszyk?", description: "Usunąć wszystkie wprowadzone pozycje i dane tego zamówienia?", confirmText: "Wyczyść", danger: true }))) return;
    setItems([]); setNotes(""); setFiles([]); setCustomerId(""); localStorage.removeItem(DRAFT_KEY);
    toast.success("Koszyk wyczyszczony");
  };

  const confirm = useConfirm();
  const submit = async (sendToProduction = false) => {
    if (isAdmin && !ownerId) return toast.error("Wybierz handlowca");
    if (!customerId) return toast.error("Wybierz klienta z listy");
    if (!items.length) return toast.error("Dodaj co najmniej jeden produkt");
    for (let i = 0; i < items.length; i++) {
      const it = items[i], n = i + 1;
      const q = Object.values(it.sizes).reduce((a, b) => a + (Number(b) || 0), 0);
      if (!it.color) return toast.error(`Pozycja ${n}: wybierz kolor`);
      if (!(Number(it.unit_price) > 0)) return toast.error(`Pozycja ${n}: podaj cenę sprzedaży netto`);
      if (q === 0) return toast.error(`Pozycja ${n}: wpisz ilości w rozmiarach`);
      if (it.branding?.type !== "Same metki" && !(it.branding?.placements || []).length) return toast.error(`Pozycja ${n}: zaznacz co najmniej jedno umiejscowienie`);
    }
    const confirmCfg = sendToProduction
      ? { title: "Zapisać i wysłać do produkcji?", description: "Zamówienie zostanie zapisane i przekazane do produkcji.", confirmText: "Zapisz i wyślij" }
      : { title: isEdit ? "Zapisać zmiany?" : "Zapisać zamówienie?", description: isEdit ? "Zapisać zmiany w tym zamówieniu?" : "Zamówienie zostanie zapisane jako szkic (status „Nowe zamówienie”). Do produkcji wyślesz je osobno.", confirmText: "Zapisz" };
    if (!(await confirm(confirmCfg))) return;
    setBusy(true);
    try {
      const payload = {
        customer_id: customerId,
        customer: null,
        items: items.map((it) => ({ ...it, unit_price: Number(it.unit_price) || 0, sizes: Object.fromEntries(Object.entries(it.sizes).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])), branding: it.branding?.type === "Same metki" ? { type: "Same metki", placements: [] } : (it.branding || { type: "DTF", placements: [] }) })),
        notes, file_ids: files.map((f) => f.id),
        employee_id: isAdmin ? ownerId : undefined,
        send_to_production: sendToProduction,
        created_at: isAdmin && isEdit && createdDate ? createdDate : undefined,
      };
      const { data } = isEdit
        ? await api.put(`/orders/${editOrder.id}`, payload)
        : await api.post("/orders", payload);
      toast.success(sendToProduction ? `Zamówienie ${data.number} wysłane do produkcji` : isEdit ? `Zamówienie ${data.number} zaktualizowane` : `Zamówienie ${data.number} zapisane`);
      if (!isEdit) { setItems([]); setNotes(""); setFiles([]); setCustomerId(""); localStorage.removeItem(DRAFT_KEY); }
      onCreated(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const changeClientOwner = async (cust) => {
    if (!cust || !changeOwnerId) return;
    try {
      const { data } = await api.post(`/customers/${cust.id}/transfer`, { employee_id: changeOwnerId, move_orders: false });
      toast.success(`Klient przypisany do handlowca: ${data.employee_name}`);
      setChangeOwnerId("");
      onCustomersChanged && onCustomersChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
      <div className="space-y-5">
        {isEdit && (
          <div data-testid="edit-order-banner" className="flex items-center justify-between rounded-xl border border-amber-500/40 bg-amber-400/10 px-5 py-3">
            <p className="text-sm font-semibold">Zamówienie w edycji <span className="font-mono text-amber-300">{editOrder.number}</span></p>
            <Button data-testid="cancel-edit-button" variant="ghost" size="sm" onClick={onCancelEdit}>Anuluj</Button>
          </div>
        )}
        {isAdmin && (
          <Section n="00" title="Handlowiec (w czyim imieniu)">
            <Select value={ownerId} onValueChange={setOwnerId} disabled={isEdit}>
              <SelectTrigger data-testid="order-owner-select"><SelectValue placeholder="Wybierz handlowca" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`owner-option-${u.id}`}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">{isEdit ? "Podczas edycji nie można zmienić przypisanego handlowca." : "Zamówienie zostanie przypisane do wybranego handlowca."}</p>
          </Section>
        )}
        {isAdmin && isEdit && (
          <Section n="00b" title="Data dodania zamówienia">
            <input type="date" data-testid="order-created-date" value={createdDate} onChange={(e) => setCreatedDate(e.target.value)}
              className="h-10 w-full sm:w-[220px] rounded-lg border border-input bg-background px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-400" />
            <p className="text-[11px] text-muted-foreground">Administrator może zmienić datę utworzenia zamówienia — wpływa na sortowanie list i statystyki.</p>
          </Section>
        )}
        <Section n="01" title="Klient">
          {customerList.length === 0 ? (
            <div className="space-y-3">
              <p data-testid="no-customers-hint" className="text-sm text-muted-foreground">Brak klientów na liście. Dodaj klienta poniżej lub w zakładce <span className="font-semibold text-amber-400">Klienci</span>.</p>
              <Button type="button" variant="outline" data-testid="order-add-customer" onClick={openCustDialog} className="gap-2 border-dashed border-amber-400/60 text-amber-300 hover:bg-amber-400/10 font-semibold"><UserPlus className="h-4 w-4" />Dodaj nowego klienta</Button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1">
                <CustomerPicker customers={customerList} value={customerId} onPick={setCustomerId} />
              </div>
              <Button type="button" variant="outline" data-testid="order-add-customer" onClick={openCustDialog} className="gap-2 border-dashed border-amber-400/60 text-amber-300 hover:bg-amber-400/10 font-semibold shrink-0"><UserPlus className="h-4 w-4" />Nowy klient</Button>
            </div>
          )}
          {isAdmin && customerId && (() => {
            const selCust = customerList.find((c) => c.id === customerId);
            const curName = users.find((u) => u.id === selCust?.employee_id)?.name || "—";
            return (
              <div data-testid="order-change-owner" className="mt-3 rounded-lg border border-border bg-background/50 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Handlowiec przypisany do klienta: <span className="text-amber-300">{curName}</span></p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Select value={changeOwnerId} onValueChange={setChangeOwnerId}>
                    <SelectTrigger data-testid="change-client-owner-select" className="h-9 w-full sm:w-[220px]"><SelectValue placeholder="Zmień handlowca klienta" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`change-owner-opt-${u.id}`}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button type="button" size="sm" variant="outline" data-testid="change-client-owner-save" disabled={!changeOwnerId || changeOwnerId === selCust?.employee_id} onClick={() => changeClientOwner(selCust)} className="h-9 gap-1.5"><UserPlus className="h-4 w-4" />Zmień handlowca</Button>
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground">Zmienia przypisanie klienta do handlowca (nie przenosi istniejących zamówień).</p>
              </div>
            );
          })()}
        </Section>

        <Section n="02" title="Produkty i rozmiary">
          {items.length === 0 && (
            <p data-testid="no-items-hint" className="text-sm text-muted-foreground">Wyszukaj produkt w katalogu i dodaj go do zamówienia. Cena sprzedaży, kolor, rozmiary i ilości pojawią się po dodaniu produktu.</p>
          )}
          {items.map((it, i) => (
            <ItemRow key={i} idx={i} item={it} products={products} catalog={catalog} rules={rules} canRemove={true} isAdmin={isAdmin} vizOptions={allVizs.filter((v) => v.client_id === customerId)}
              onDuplicate={() => setItems((prev) => prev.flatMap((x, j) => (j === i ? [x, { ...x, sizes: { ...x.sizes }, branding: { ...(x.branding || {}), placements: [...((x.branding || {}).placements || [])] } }] : [x])))}
              onChange={(v) => setItems((prev) => prev.map((x, j) => (j === i ? v : x)))} onRemove={() => setItems((prev) => prev.filter((_, j) => j !== i))} />
          ))}
          <ProductPicker products={products} premiumSkus={premiumSkus} testid="add-item-button" highlight={items.length > 0} label={items.length ? "Dodaj kolejny produkt" : "Wyszukaj i dodaj produkt"} onPick={addItem} />
        </Section>

        <Section n="03" title="Uwagi i pliki graficzne">
          <Textarea data-testid="order-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Uwagi do zamówienia, terminy, szczegóły nadruku…" rows={3} />
          <FileUploader files={files} onChange={setFiles} />
        </Section>

        {customerId && clientVizList.length > 0 && (
          <Section n="04" title="Wizualizacje klienta">
            <p className="text-[11px] text-muted-foreground">Zaznacz wizualizacje z kartoteki, które mają trafić do tego zamówienia — zostaną dołączone jako PDF i będą widoczne dla produkcji.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="client-viz-list">
              {clientVizList.map((v) => {
                const on = isClientVizAttached(v);
                const thumb = vizThumbId(v);
                const dstr = v.created_at ? new Date(v.created_at).toLocaleDateString("pl-PL") : "";
                const attachId = vizAttachFileId(v);
                return (
                  <label key={v.id} data-testid={`client-viz-${v.id}`} htmlFor={`client-viz-chk-${v.id}`}
                    className={`flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer transition-colors ${on ? "border-amber-400 bg-amber-400/10" : "border-border hover:border-amber-400/50"}`}>
                    <Checkbox id={`client-viz-chk-${v.id}`} data-testid={`client-viz-check-${v.id}`} checked={on} onCheckedChange={() => toggleClientViz(v)} />
                    {thumb
                      ? <img src={`${API}/files/${thumb}`} alt="" className="h-12 w-12 shrink-0 rounded object-contain bg-[#0D1117]" />
                      : <div className="h-12 w-12 shrink-0 rounded bg-[#0D1117]" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{vizTitle(v)}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {dstr && <>Utworzono: {dstr}</>}
                        {!attachId && <span className="text-amber-400/90"> · brak zapisanego arkusza</span>}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </Section>
        )}
      </div>

      <aside className="lg:sticky lg:top-20 rounded-xl border border-amber-500/40 bg-card p-5 space-y-4" data-testid="order-summary">
        <p className="font-mono text-[11px] uppercase tracking-widest text-amber-400">Podsumowanie</p>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Sztuki</span><span data-testid="summary-qty" className="font-mono">{totals.qty}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Netto</span><span data-testid="summary-net" className="font-mono">{pln(totals.net)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">VAT 23%</span><span className="font-mono">{pln(totals.net * 0.23)}</span></div>
          <div className="flex justify-between font-semibold"><span>Brutto</span><span className="font-mono">{pln(totals.net * 1.23)}</span></div>
        </div>
        <div className="rounded-lg bg-amber-400 text-[#0D1117] p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest">Twoja prowizja</p>
          <p data-testid="summary-commission" className="font-display text-3xl font-extrabold tabular-nums">{pln(totals.comm)}</p>
          {totals.indiv && <p className="text-xs mt-1 font-medium">Część pozycji wymaga indywidualnego ustalenia.</p>}
        </div>
        <Button data-testid="create-order-button" onClick={() => submit(false)} disabled={busy} className="w-full h-12 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold uppercase tracking-wider">
          {busy ? "Zapisywanie…" : isEdit ? "Zapisz zmiany" : "Zapisz"}
        </Button>
        {(!isEdit || editOrder.status === "Nowe zamówienie") && (
          <Button data-testid="save-send-production-button" onClick={() => submit(true)} disabled={busy} className="w-full h-12 bg-indigo-500 text-white hover:bg-indigo-600 font-bold uppercase tracking-wider gap-2">
            <Send className="h-4 w-4" />Zapisz i wyślij do produkcji
          </Button>
        )}
        {isEdit && (
          <Button data-testid="cancel-edit-exit-button" variant="outline" onClick={onCancelEdit} disabled={busy} className="w-full h-11 font-bold uppercase tracking-wider gap-2 border-border text-muted-foreground hover:text-foreground hover:bg-secondary">
            <X className="h-4 w-4" />Anuluj i wyjdź
          </Button>
        )}
        {!isEdit && (items.length > 0 || notes || files.length > 0 || customerId) && (
          <Button data-testid="clear-cart-button" variant="ghost" onClick={clearCart} className="w-full text-rose-400 hover:text-rose-300 gap-2"><Trash2 className="h-4 w-4" />Wyczyść koszyk</Button>
        )}
        {!isEdit && <p className="text-[11px] text-emerald-400/90 flex items-center gap-1.5"><Save className="h-3 w-3 shrink-0" />Koszyk zapisywany automatycznie — możesz zmienić zakładkę lub odświeżyć i wrócić do niego.</p>}
        <p className="text-[11px] text-muted-foreground">{isEdit ? (isAdmin ? "Jako administrator możesz edytować to zamówienie w dowolnym statusie." : "Edycja możliwa tylko w statusie „Nowe zamówienie”.") : <><b>Zapisz</b> = szkic (status <span className="text-blue-300 font-semibold">Nowe zamówienie</span>), niewidoczny dla produkcji. <b>Zapisz i wyślij do produkcji</b> przekazuje zamówienie do kolejki produkcji.</>}</p>
      </aside>

      <Dialog open={custOpen} onOpenChange={setCustOpen}>
        <DialogContent data-testid="order-customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">Nowy klient</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">{isAdmin ? `Klient zostanie przypisany do handlowca: ${users.find((u) => u.id === ownerId)?.name || "—"}.` : "Wypełnij dane kontrahenta."} Pola oznaczone * są wymagane.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1"><Label className="text-xs">NIP</Label><Input data-testid="order-cust-nip" value={newCust.nip} onChange={(e) => setNewCust({ ...newCust, nip: e.target.value })} className="mt-1" placeholder="10 cyfr" /></div>
              <Button data-testid="order-cust-gus" type="button" variant="outline" onClick={fetchGusNew} disabled={custBusy} className="gap-2 border-dashed"><Search className="h-4 w-4" />Pobierz dane po NIP</Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Uzupełni nazwę firmy i adres z rejestru (Biała Lista MF). Osobę kontaktową, telefon i e-mail wpisz ręcznie.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {CUST_FIELDS.map(([k, l]) => <div key={k}><Label className="text-xs">{l}</Label><Input data-testid={`order-cust-${k}`} value={newCust[k]} onChange={(e) => setNewCust({ ...newCust, [k]: e.target.value })} className="mt-1" /></div>)}
            </div>
            <Button data-testid="order-cust-save" onClick={saveNewCustomer} disabled={custBusy} className="w-full bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">{custBusy ? "Zapisywanie…" : "Dodaj klienta"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
